import { useState, useEffect, useCallback, useRef } from 'react';
import SFSaleApiClient from '../services/SFSaleApiClient';

/**
 * SF Sale Order Workflow hook - handles Salesforce sale order workflow execution
 * Uses /api/sale-sf/workflows endpoints
 */
export const useSFSaleWorkflow = (workflowId, token, initialRunId = null) => {
  // Core state
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [runId, setRunId] = useState(initialRunId);
  const [executionStatus, setExecutionStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [lastEventTimestamp, setLastEventTimestamp] = useState(null);

  // Services
  const apiClientRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastEventTimestampRef = useRef(null);
  const isPollingRef = useRef(false); // Prevent concurrent polling requests

  // Process messages helper
  // includeUserMessages: true when loading historical chat, false during active polling (user messages added locally)
  const processMessagesWithTiming = useCallback((newEvents, skipDelay = false, includeUserMessages = false) => {
    console.log('🔵 SFSaleWorkflow: Processing events', newEvents.length, 'includeUser:', includeUserMessages);

    // Only include 'user' messages when loading historical chat (not during active polling)
    const allowedTypes = includeUserMessages
      ? ['user', 'assistant', 'tool_call', 'tool_result', 'workflow_step']
      : ['assistant', 'tool_call', 'tool_result', 'workflow_step'];

    const chatMessages = newEvents
      .filter(update => update.type === 'chat_message' &&
             allowedTypes.includes(update.data.message_type))
      .map(update => ({
        id: update.data.message_id,
        type: update.data.message_type,
        content: update.data.content,
        timestamp: update.data.timestamp,
        step_name: update.data.step_name,
        tool_name: update.data.tool_name
      }));

    // Process UI component events
    const uiComponentEvents = newEvents
      .filter(update => update.type === 'workflow_event' &&
             update.data.event_type === 'step.completed' &&
             update.data.data?.structured_output?.component_definition)
      .map(update => ({
        id: `ui_${update.data.data.step_name}_${update.data.timestamp}`,
        type: 'assistant',
        content: update.data.data.structured_output,
        timestamp: update.data.timestamp,
        step_name: update.data.data.step_name,
        isUIComponent: true
      }));

    const allMessages = [...chatMessages, ...uiComponentEvents]
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    if (allMessages.length === 0) return;

    setMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const newMessages = allMessages.filter(msg => !existingIds.has(msg.id));
      return [...prev, ...newMessages];
    });
  }, []);

  // Polling mechanism - increased interval to 1500ms and added lock to prevent concurrent requests
  const startPollingForResults = useCallback((currentRunId) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    console.log('🔵 SFSaleWorkflow: Starting polling for run', currentRunId);
    isPollingRef.current = false; // Reset the lock

    pollingIntervalRef.current = setInterval(async () => {
      // Skip if a request is already in progress
      if (isPollingRef.current) {
        console.log('🔵 SFSaleWorkflow: Skipping poll - request in progress');
        return;
      }

      isPollingRef.current = true;
      try {
        const status = await apiClientRef.current.getWorkflowStatus(currentRunId);

        if (status?.success && status.data) {
          const workflowRun = status.data.workflow_run;
          const currentStatus = workflowRun?.status;
          const stepName = workflowRun?.current_step;

          if (currentStatus) setExecutionStatus(currentStatus);
          if (stepName) setCurrentStep(stepName);

          // Fetch events only if not in terminal state (to get final events)
          try {
            const events = await apiClientRef.current.getWorkflowEvents(currentRunId, lastEventTimestampRef.current);
            if (events?.success && events.updates) {
              processMessagesWithTiming(events.updates, true);

              if (events.updates.length > 0) {
                const latestTimestamp = events.updates[events.updates.length - 1].timestamp;
                lastEventTimestampRef.current = latestTimestamp;
                setLastEventTimestamp(latestTimestamp);
              }
            }
          } catch (eventsError) {
            console.error('🔵 SFSaleWorkflow: Error fetching events:', eventsError);
          }

          // Stop polling on terminal state
          if (['completed', 'failed', 'cancelled', 'paused'].includes(currentStatus)) {
            console.log('🔵 SFSaleWorkflow: Workflow reached terminal state:', currentStatus);
            setIsLoading(false);

            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }
      } catch (error) {
        console.error('🔵 SFSaleWorkflow: Polling error:', error);
      } finally {
        isPollingRef.current = false;
      }
    }, 1500); // Increased from 250ms to 1500ms to reduce server load
  }, [processMessagesWithTiming]);

  // Initialize services
  useEffect(() => {
    if (!token) return;

    apiClientRef.current = new SFSaleApiClient(token);

    if (initialRunId) {
      console.log('🔵 SFSaleWorkflow: Continuing existing workflow:', initialRunId);
      setExecutionStatus('running');
      setIsLoading(true);

      (async () => {
        try {
          const allEvents = await apiClientRef.current.getWorkflowEvents(initialRunId, null);
          if (allEvents?.success && allEvents.updates) {
            // Load historical - include user messages from DB
            processMessagesWithTiming(allEvents.updates, true, true);

            if (allEvents.updates.length > 0) {
              const latestTimestamp = allEvents.updates[allEvents.updates.length - 1].timestamp;
              lastEventTimestampRef.current = latestTimestamp;
            }
          }
          startPollingForResults(initialRunId);
        } catch (error) {
          console.error('🔵 SFSaleWorkflow: Error loading messages:', error);
          startPollingForResults(initialRunId);
        }
      })();
    } else {
      setMessages([{
        id: 'welcome',
        type: 'system',
        content: `🔵 Ready to start Salesforce Sale Order workflow. Upload a purchase order to begin!`,
        timestamp: new Date().toISOString()
      }]);
      setExecutionStatus('ready');
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [token, initialRunId, startPollingForResults, processMessagesWithTiming]);

  // Send message function
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || !apiClientRef.current) return;

    if (!runId && isLoading) {
      console.log('🔵 SFSaleWorkflow: Blocked duplicate start');
      return;
    }

    setIsLoading(true);
    setError(null);

    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      if (!runId) {
        console.log('🔵 SFSaleWorkflow: Starting new workflow', { workflowId, messageText });

        const result = await apiClientRef.current.startWorkflow(workflowId, messageText);

        if (result.success && result.run_id) {
          lastEventTimestampRef.current = null;
          setMessages([userMessage]);
          setEvents([]);
          setError(null);
          setRunId(result.run_id);
          setExecutionStatus('running');
          startPollingForResults(result.run_id);
        } else {
          throw new Error('Failed to start workflow');
        }
      } else {
        console.log('🔵 SFSaleWorkflow: Continuing workflow', { runId, messageText });

        const result = await apiClientRef.current.continueWorkflow(runId, messageText);

        if (result.success) {
          setExecutionStatus('running');
          lastEventTimestampRef.current = null;
          startPollingForResults(runId);
        } else {
          throw new Error('Failed to continue workflow');
        }
      }
    } catch (err) {
      console.error('🔵 SFSaleWorkflow: Error:', err);

      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        type: 'error',
        content: `❌ Error: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
      setError(err.message);
      setExecutionStatus('failed');
      setIsLoading(false);
    }
  }, [workflowId, runId, isLoading, startPollingForResults]);

  // Reset workflow
  const resetWorkflow = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setRunId(null);
    setMessages([{
      id: 'welcome',
      type: 'system',
      content: `🔵 Ready to start Salesforce Sale Order workflow. Upload a purchase order to begin!`,
      timestamp: new Date().toISOString()
    }]);
    setExecutionStatus('ready');
    setEvents([]);
    setError(null);
    setIsLoading(false);
    setLastEventTimestamp(null);
    lastEventTimestampRef.current = null;
  }, []);

  return {
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    events,
    error,
    sendMessage,
    resetWorkflow,
    hasActiveWorkflow: !!runId,
    canSendMessage: !isLoading && !!token
  };
};
